import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';

mermaid.initialize({
  startOnLoad: false,
  theme: 'base',
  themeVariables: {
    primaryColor: '#C1440E',
    primaryTextColor: '#E8E8F5',
    primaryBorderColor: '#7A1E07',
    lineColor: '#E5622D',
    secondaryColor: '#1E1E38',
    tertiaryColor: '#141428',
    background: '#0D0D1A',
    mainBkg: '#1E1E38',
    nodeBorder: '#3D3D6B',
    clusterBkg: '#141428',
    titleColor: '#E8E8F5',
    edgeLabelBackground: '#1E1E38',
    fontFamily: 'DM Sans, system-ui, sans-serif',
  },
  flowchart: { curve: 'basis', padding: 20 },
  securityLevel: 'loose',
});

// Global counter: generates a UNIQUE ID every render to prevent Mermaid's
// internal element-ID collision which causes silent blank renders.
let chartCounter = 0;

/**
 * MermaidChart — renders a Mermaid.js diagram from a code string.
 *
 * Props:
 *   code      {string}  - Mermaid syntax string
 *   className {string}  - Optional wrapper class
 *   onRenderStart {fn}  - Called when rendering begins
 *   onRenderDone  {fn}  - Called when rendering finishes (success or error)
 */
export default function MermaidChart({ code, className = '', onRenderStart, onRenderDone }) {
  const containerRef = useRef(null);
  const [error, setError]       = useState(null);
  const [rendering, setRendering] = useState(false);
  // Track the last code we successfully rendered to skip no-op re-renders
  const lastCodeRef = useRef(null);

  useEffect(() => {
    if (!code || !containerRef.current) return;
    // Skip if code hasn't changed since last successful render
    if (code === lastCodeRef.current) return;

    setError(null);
    setRendering(true);
    onRenderStart?.();

    // Generate a fresh unique ID every render — avoids Mermaid's ID-collision bug
    const uniqueId = `mermaid-chart-${++chartCounter}`;

    // Remove any previously leaked Mermaid error elements from document.body before rendering.
    // Mermaid v11 inserts error nodes outside React's tree on render failure;
    // they survive route navigation unless we clean them up explicitly.
    const purgeLeak = (id) => {
      const el = document.getElementById(id);
      if (el && !containerRef.current?.contains(el)) el.remove();
    };

    // Defer render until after the DOM is fully painted (fixes blank on first mount)
    const rafId = requestAnimationFrame(() => {
      // Purge any stale Mermaid node that was left from a prior failed render
      document.querySelectorAll('[id^="mermaid-chart-"]').forEach(el => {
        if (!containerRef.current?.contains(el)) el.remove();
      });

      const applySvg = (svg) => {
        if (!containerRef.current) return;
        containerRef.current.innerHTML = svg;
        const svgEl = containerRef.current.querySelector('svg');
        if (svgEl) {
          svgEl.removeAttribute('width');
          svgEl.removeAttribute('height');
          svgEl.style.maxWidth = '100%';
          svgEl.style.width    = '100%';
          svgEl.style.height   = 'auto';
          svgEl.style.display  = 'block';
        }
      };

      // Strip emojis + class syntax for a safe fallback render
      const stripForFallback = (src) => src
        .replace(/[\u{1F300}-\u{1F9FF}\u{1FA00}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}]/gu, '')
        .replace(/:::[\w-]+/g, '')
        .replace(/^\s*classDef\s+.*/gm, '')
        .replace(/^\s*class\s+.*/gm, '')
        .trim();

      mermaid.render(uniqueId, code)
        .then(({ svg }) => {
          lastCodeRef.current = code;
          applySvg(svg);
        })
        .catch(async (err) => {
          purgeLeak(uniqueId);
          console.warn('[MermaidChart] First render failed, trying fallback:', err.message ?? err);

          // Fallback: strip class assignments and emojis, then retry
          const fallbackCode = stripForFallback(code);
          if (fallbackCode && fallbackCode !== code) {
            const fallbackId = `mermaid-chart-${++chartCounter}-fb`;
            try {
              const { svg: fbSvg } = await mermaid.render(fallbackId, fallbackCode);
              lastCodeRef.current = code;
              applySvg(fbSvg);
              return; // Rendered successfully via fallback — no error shown
            } catch (fbErr) {
              purgeLeak(fallbackId);
              console.error('[MermaidChart] Fallback render also failed:', fbErr.message ?? fbErr);
            }
          }

          setError('Could not render the flowchart. Click Regenerate to get a fresh diagram.');
        })
        .finally(() => {
          setRendering(false);
          onRenderDone?.();
        });
    });

    return () => {
      cancelAnimationFrame(rafId);
      // On unmount or code change, remove any in-flight leaked Mermaid error node
      purgeLeak(uniqueId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  if (error) {
    return (
      <div style={{
        padding: '14px 18px', color: '#EF4444',
        background: 'rgba(239,68,68,0.06)',
        border: '1px solid rgba(239,68,68,0.2)',
        borderRadius: '10px', fontSize: '13px',
      }}>
        ⚠️ {error}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {rendering && (
        <div className="mermaid-rendering-overlay">
          <div className="mermaid-dots">
            <span /><span /><span />
          </div>
          <p>Rendering diagram…</p>
        </div>
      )}
      <div
        ref={containerRef}
        className={className}
        style={{
          width: '100%',
          minWidth: 0,
          overflow: 'hidden',
          opacity: rendering ? 0 : 1,
          transition: 'opacity 0.35s ease',
        }}
      />
    </div>
  );
}
